import { extendTheme } from '@mui/joy';

import { borderRadius } from './borderRadius';
import { brand } from './brand';
import { dark } from './dark';
import { light } from './light';
import { typography } from './typography';

export const theme = extendTheme({
  colorSchemes: {
    light: { palette: light },
    dark: { palette: dark },
  },
  fontFamily: {
    body: 'Lato',
  },
  fontWeight: {
    xs: 300,
    sm: 400,
  },
  typography,
  radius: borderRadius,
  components: {
    JoyTypography: {
      styleOverrides: {
        root: ({ theme, ownerState }) => ({
          fontSize: (() => {
            switch (ownerState.component) {
              case 'h1':
                return '40px';
              case 'h2':
                return '36px';
              case 'h4':
                return '24px';
                return;
            }
          })(),
          fontWeight: (() => {
            switch (ownerState.level) {
              case 'body-xs':
                return 400;
            }
          })(),
          color: ownerState.color === 'primary' ? theme.palette.text.primary : '',
        }),
      },
    },
    JoyAvatar: {
      styleOverrides: {
        root: ({ ownerState, theme }) => ({
          color: ownerState.variant === 'outlined' ? theme.palette.text.secondary : 'inherit',
          borderColor: ownerState.variant === 'outlined' ? theme.palette.text.icon : 'inherit',
        }),
      },
    },
    JoySelect: {
      defaultProps: {
        indicator: null,
      },
      styleOverrides: {
        root: ({ ownerState, theme }) => ({
          ':hover': {
            background:
              ownerState.variant === 'outlined' ? theme.palette.primary.outlinedColor : 'inherit',
            borderColor:
              ownerState.variant === 'outlined'
                ? theme.palette.primary.outlinedHoverBorder
                : 'inherit',
          },
        }),
      },
    },
    JoyInput: {
      styleOverrides: {
        root: ({ ownerState, theme }) => ({
          boxShadow: 'none',
          color: ownerState.variant === 'outlined' ? theme.palette.text.secondary : 'inherit',
          '--Input-focusedHighlight':
            ownerState.variant === 'outlined'
              ? `${brand.primary.dark['800']} !important`
              : 'inherit',
          '&.Mui-focused': {
            color: ownerState.variant === 'outlined' ? theme.palette.text.primary : 'inherit',
          },
          '&.Mui-disabled': {
            color: theme.palette.text.secondary,
          },
        }),
      },
    },
    JoyButton: {
      styleOverrides: {
        root: ({ ownerState, theme }) => {
          const sizeTokens: Record<string, object> = {
            sm: {
              '--Button-minHeight': '32px',
              fontSize: '14px',
              lineHeight: '14px',
              '--Button-gap': '4px',
              '--Button-paddingInline': '12px',
              '--Button-iconOffsetStep': '4px',
              '--Button-borderWidth': '1px',
              '--Button-focusBorderWidth': '3px',
            },
            md: {
              '--Button-minHeight': '40px',
              fontSize: '14px',
              lineHeight: '14px',
              '--Button-gap': '6px',
              '--Button-paddingInline': '16px',
              '--Button-iconOffsetStep': '4px',
              '--Button-borderWidth': '2px',
              '--Button-focusBorderWidth': '3px',
            },
            lg: {
              '--Button-minHeight': '48px',
              fontSize: '16px',
              lineHeight: '16px',
              '--Button-gap': '8px',
              '--Button-paddingInline': '16px',
              '--Button-iconOffsetStep': '4px',
              '--Button-borderWidth': '2px',
              '--Button-focusBorderWidth': '3px',
            },
          };

          // Color & background styles — applied to ALL buttons (including ButtonGroup)
          const variantStyles: Record<string, object> = {
            solid: {
              backgroundColor: theme.palette.primary.solidBg,
              color: theme.palette.primary.solidColor,
              '&:hover': {
                backgroundColor: theme.palette.primary.solidHoverBg,
              },
              '&:focus-visible': {
                backgroundColor: theme.palette.primary.solidHoverBg,
                boxShadow: `inset 0 0 0 3px ${theme.palette.primary.softActiveColor}`,
                outline: 'none',
              },
              '&:active': {
                backgroundColor: theme.palette.primary.solidActiveBg,
                boxShadow: 'none',
              },
              '&.Mui-disabled': {
                [theme.getColorSchemeSelector('light')]: {
                  backgroundColor: brand.neutral['150'],
                  color: brand.neutral['400'],
                },
                [theme.getColorSchemeSelector('dark')]: {
                  backgroundColor: brand.neutral['700'],
                  color: brand.neutral['600'],
                },
              },
            },
            secondary: {
              backgroundColor: theme.palette.background.surface,
              color: theme.palette.text.secondary,
              '&:hover': {
                backgroundColor: theme.palette.primary.outlinedHoverBg,
              },
              '&:focus-visible': {
                backgroundColor: theme.palette.primary.outlinedHoverBg,
                outline: 'none',
              },
              '&:active': {
                backgroundColor: theme.palette.primary.softActiveBg,
              },
              '&.Mui-disabled': {
                backgroundColor: theme.palette.background.surface,
                color: theme.palette.primary.outlinedDisabledColor,
              },
            },
            outlined: {
              backgroundColor: theme.palette.primary.outlinedColor,
              color: theme.palette.text.primary,
              '&:hover': {
                backgroundColor: theme.palette.primary.outlinedHoverBg,
              },
              '&:focus-visible': {
                backgroundColor: theme.palette.primary.outlinedHoverBg,
                outline: 'none',
              },
              '&:active': {
                backgroundColor: theme.palette.primary.softActiveBg,
              },
              '&.Mui-disabled': {
                backgroundColor: theme.palette.primary.outlinedColor,
                color: theme.palette.primary.outlinedDisabledColor,
              },
            },
            cta: {
              backgroundColor: theme.palette.purple?.solidBg,
              color: theme.palette.purple?.solidColor,
              '&:hover': {
                backgroundColor: theme.palette.purple?.solidHoverBg,
              },
              '&:focus-visible': {
                backgroundColor: theme.palette.purple?.solidHoverBg,
                boxShadow: `inset 0 0 0 3px ${theme.palette.purple?.outlinedBorder}`,
                outline: 'none',
              },
              '&:active': {
                backgroundColor: theme.palette.purple?.solidActiveBg,
                boxShadow: 'none',
              },
              '&.Mui-disabled': {
                [theme.getColorSchemeSelector('light')]: {
                  backgroundColor: brand.neutral['150'],
                  color: brand.neutral['400'],
                },
                [theme.getColorSchemeSelector('dark')]: {
                  backgroundColor: brand.neutral['700'],
                  color: brand.neutral['600'],
                },
              },
            },
            danger: {
              backgroundColor: theme.palette.danger.solidBg,
              color: theme.palette.danger.solidColor,
              '&:hover': {
                backgroundColor: theme.palette.danger.solidHoverBg,
              },
              '&:focus-visible': {
                backgroundColor: theme.palette.danger.solidHoverBg,
                outline: 'none',
                [theme.getColorSchemeSelector('light')]: {
                  boxShadow: `inset 0 0 0 3px ${brand.danger['200']}`,
                },
                [theme.getColorSchemeSelector('dark')]: {
                  boxShadow: `inset 0 0 0 3px ${brand.danger['400']}`,
                },
              },
              '&:active': {
                backgroundColor: theme.palette.danger.solidActiveBg,
                boxShadow: 'none',
              },
              '&.Mui-disabled': {
                [theme.getColorSchemeSelector('light')]: {
                  backgroundColor: brand.neutral['150'],
                  color: brand.neutral['400'],
                },
                [theme.getColorSchemeSelector('dark')]: {
                  backgroundColor: brand.neutral['700'],
                  color: brand.neutral['600'],
                },
              },
            },
            plain: {
              '&:hover': {
                background: 'transparent',
              },
            },
          };

          // Border styles — only for standalone buttons (ButtonGroup handles its own borders)
          const variantBorderStyles: Record<string, object> = {
            solid: {
              border: 'none',
              '&.Mui-disabled': { border: 'none' },
            },
            secondary: {
              border: `var(--Button-borderWidth, 2px) solid ${theme.palette.primary.solidBg}`,
              '&:focus-visible': {
                borderWidth: 'var(--Button-focusBorderWidth, 3px)',
              },
              '&:active': {
                borderWidth: 'var(--Button-borderWidth, 2px)',
              },
              '&.Mui-disabled': {
                borderColor: theme.palette.primary.outlinedDisabledColor,
              },
            },
            outlined: {
              border: `var(--Button-borderWidth, 2px) solid ${theme.palette['border']['border']}`,
              '&:hover': {
                borderColor: theme.palette.primary.outlinedHoverBorder,
              },
              '&:focus-visible': {
                borderColor: theme.palette.primary.outlinedHoverBorder,
                borderWidth: 'var(--Button-focusBorderWidth, 3px)',
              },
              '&:active': {
                borderColor: theme.palette.primary.outlinedHoverBorder,
                borderWidth: 'var(--Button-borderWidth, 2px)',
              },
              '&.Mui-disabled': {
                borderColor: theme.palette.primary.outlinedDisabledColor,
              },
            },
            cta: {
              border: 'none',
              '&.Mui-disabled': { border: 'none' },
            },
            danger: {
              border: 'none',
              '&.Mui-disabled': { border: 'none' },
            },
          };

          return {
            width: 'fit-content',
            userSelect: 'none',
            fontWeight: 400,
            boxSizing: 'border-box',
            ...(sizeTokens[ownerState.size as string] ?? sizeTokens.md),
            ...(variantStyles[ownerState.variant as string] ?? {}),
            '&:where(:not(.MuiButtonGroup-root *))': {
              borderRadius: '8px',
              ...(variantBorderStyles[ownerState.variant as string] ?? {}),
            },
          };
        },
        startDecorator: {
          marginLeft: 'calc(var(--Button-iconOffsetStep, 4px) * -1)',
        },
        endDecorator: {
          marginRight: 'calc(var(--Button-iconOffsetStep, 4px) * -1)',
        },
      },
    },
    JoyMenuButton: {
      styleOverrides: {
        root: () => ({
          '&:hover': {
            background: 'transparent',
          },
        }),
      },
    },
    JoyChip: {
      styleOverrides: {
        root: ({ theme, ownerState }) => ({
          color: (() => {
            if (ownerState.variant === 'outlined') {
              return theme.palette.text.primary;
            }
            if (ownerState.variant === 'solid') {
              return theme.palette.primary.solidColor;
            }
            return 'inherit';
          })(),
        }),
      },
    },
    JoyTab: {
      styleOverrides: {
        root: ({ theme, ownerState }) => ({
          '&:hover': {
            color: theme.palette.text.secondary,
          },
          '&:active, &:focus': {
            color: theme.palette.text.primary,
          },
          '::after': {
            background: ownerState.selected
              ? `${theme.palette.primary.outlinedActiveBg} !important`
              : null,
          },
          '& > p': {
            color: `${ownerState.selected ? theme.palette.text.primary : theme.palette.text.secondary} !important`,
            fontWeight: theme.fontWeight.md,
          },
        }),
      },
    },
    JoySkeleton: {
      styleOverrides: {
        root: ({ theme }) => ({
          '::before': {
            background: `${theme.palette.background.level2} !important`,
          },
        }),
      },
    },
    JoyIconButton: {
      styleOverrides: {
        root: ({ theme, ownerState }) => ({
          color: ownerState.variant === 'outlined' ? theme.palette.text.primary : 'inherit',
          '&:hover': {
            background: ownerState.variant === 'plain' ? 'transparent' : 'inherit',
            borderColor:
              ownerState.variant === 'outlined'
                ? theme.palette.primary.outlinedHoverBorder
                : 'inherit',
            color: 'inherit',
          },
        }),
      },
    },
    JoyButtonGroup: {
      styleOverrides: {
        root: ({ theme, ownerState }) => ({
          color: ownerState.variant === 'outlined' ? theme.palette.text.primary : 'inherit',
          '&:active, &:focus': {
            color: ownerState.variant === 'outlined' ? theme.palette.common.white : 'inherit',
          },
        }),
      },
    },
    JoyMenu: {
      styleOverrides: {
        root: ({ theme }) => ({
          paddingY: 0.5,
          border: `1px solid ${theme.palette.primary.outlinedBorder}`,
          boxShadow: theme.shadow.md,
        }),
      },
    },
    JoyMenuItem: {
      styleOverrides: {
        root: ({ theme }) => ({
          '&:hover': {
            background: `${theme.palette.primary.outlinedHoverBg} !important`,
          },
        }),
      },
    },
    JoyRadio: {
      styleOverrides: {
        radio: ({ theme, ownerState }) => ({
          background: ownerState.checked
            ? theme.palette.primary.outlinedActiveBg
            : theme.palette.background.surface,
        }),
        icon: ({ theme, ownerState }) => ({
          background: ownerState.checked
            ? theme.palette.common.white
            : theme.palette.background.surface,
        }),
      },
    },
    JoyAccordionSummary: {
      styleOverrides: {
        root: ({ theme, ownerState }) => {
          return {
            '&:hover': {
              background:
                ownerState.variant === 'plain' ? theme.palette.primary.outlinedHoverBg : 'inherit',
              borderRadius: ownerState.variant === 'plain' ? theme.radius.sm : 'inherit',
            },
            '&:active, &:focus': {
              border:
                ownerState.variant === 'plain'
                  ? `1px solid ${theme.palette.primary.outlinedActiveBg}`
                  : 'inherit',
            },
          };
        },
      },
    },
    JoyCard: {
      styleOverrides: {
        root: ({ theme, ownerState }) => ({
          padding: theme.spacing(3),
          borderColor: theme.palette['border']['border'],
          border:
            ownerState.variant === 'soft'
              ? `1px solid ${theme.palette.primary.outlinedBorder}`
              : '',
        }),
      },
    },
    JoyModalDialog: {
      styleOverrides: {
        root: ({ theme }) => ({
          boxShadow: 'none',
          borderColor: theme.palette['border']['border'],
        }),
      },
    },
    JoyLink: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette['hyperlink']['text'],
          textDecoration: 'none',
          '&:hover': {
            textDecoration: 'underline',
          },
        }),
      },
      defaultProps: {
        underline: 'hover',
      },
    },
    JoyTooltip: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.text.primary,
          background: theme.palette.background.tooltip,
          typography: 'body-md',
          fontSize: theme.vars.fontSize.md,
          lineHeight: theme.vars.lineHeight.md,
          '.MuiTypography-root': {
            color: theme.palette.text.primary,
          },
        }),
      },
    },
  },
});
